import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Users, 
  ArrowLeft, 
  Building2, 
  UserCheck, 
  TrendingUp, 
  Target,
  FileText, 
  AlertTriangle, 
  Info, 
  CheckCircle, 
  XCircle,
  Linkedin,
  Network,
  Crown,
  Briefcase,
  MessageSquare,
  BarChart3,
  Sparkles
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import InnerCircleSearch from "@/components/InnerCircleSearch";

export default function InnerCircleReport() {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-indigo-50/50 p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/jobintel-360-reports')} 
                className="h-9 w-9 p-0 bg-white"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Inner Circle Networking Report</h1>
                <p className="text-muted-foreground mt-1">
                  Your direct line to the hidden job market decision-makers!
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/inner-circle-reports')} 
              className="gap-2 bg-white"
            >
              <FileText className="h-4 w-4" />
              My Reports
            </Button>
          </div>

          {/* Hero Card */}
          <Card className="border-2 border-indigo-400 bg-indigo-50 shadow-lg">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                  Access the Hidden Job Market Through Direct Connections
                </h2>
                <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed px-2">
                  While most job seekers spray resumes into the void and hope HR calls back, this report puts you 
                  face-to-face with the hiring authorities. <span className="text-destructive font-bold">70-80% of jobs</span> are 
                  filled through networking—not job boards. Get <span className="text-primary font-bold">500-1,000 decision-maker contacts</span> at 
                  100 companies matched to your background, with <span className="font-bold">15-30% response rates</span> vs. 
                  the <span className="text-destructive font-bold">&lt;3%</span> from cold applications.
                </p>
              </div>

              {/* Two Info Links */}
              <div className="flex items-center justify-center gap-6 mt-4 mb-8">
                {/* Link 1: Why you need this (Amber themed) */}
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="inline-flex items-center text-amber-600 hover:text-amber-700 underline decoration-amber-400 underline-offset-4 font-semibold transition-colors">
                      <AlertTriangle className="w-4 h-4 mr-1.5" />
                      Why you need this
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[85vh]">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-amber-600">
                        <AlertTriangle className="w-5 h-5" />
                        Why You Need Direct Access to Decision-Makers
                      </DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="max-h-[65vh] pr-4">
                      <div className="space-y-6">
                        {/* Section 1: The Hidden Job Market */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <h3 className="font-semibold text-amber-800 mb-2">The Hidden Job Market Is Massive</h3>
                          <p className="text-sm text-amber-900/80 mb-3">
                            <span className="font-bold text-amber-700">70-80% of jobs</span> are filled through networking, 
                            not traditional job postings. That means only 20-30% of opportunities are ever publicly 
                            advertised—the rest are found via relationships.
                          </p>
                          <div className="bg-white rounded-lg p-3 border border-amber-200">
                            <div className="text-center">
                              <div className="text-3xl font-bold text-amber-600">70-80%</div>
                              <div className="text-sm text-muted-foreground">of jobs filled through networking</div>
                            </div>
                          </div>
                        </div>

                        {/* Section 2: Why Referrals Win */}
                        <div>
                          <h3 className="font-semibold text-foreground mb-3">Referrals Are the #1 Way Candidates Get Hired</h3>
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-muted-foreground">Employee referrals account for ~40% of hires</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-muted-foreground">Referred candidates are 4-5x more likely to be hired</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-muted-foreground">Companies fill 7 out of 10 positions through direct referrals</p>
                            </div>
                          </div>
                        </div>

                        <Separator />

                        {/* Section 3: The Statistics */}
                        <div>
                          <h3 className="font-semibold text-foreground mb-3">The Hard Truth: Job Search Statistics</h3>
                          <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-muted/50">
                                <tr>
                                  <th className="text-left p-3 font-medium">Metric</th>
                                  <th className="text-right p-3 font-medium">Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr className="border-t">
                                  <td className="p-3">Jobs filled through networking</td>
                                  <td className="p-3 text-right font-semibold text-success">70-80%</td>
                                </tr>
                                <tr className="border-t bg-red-50">
                                  <td className="p-3">Cold application interview rate</td>
                                  <td className="p-3 text-right font-semibold text-destructive">&lt;3%</td>
                                </tr>
                                <tr className="border-t">
                                  <td className="p-3">Hires without public job postings</td>
                                  <td className="p-3 text-right font-semibold">Up to 50%</td>
                                </tr>
                                <tr className="border-t bg-green-50">
                                  <td className="p-3">Personalized outreach response rate</td>
                                  <td className="p-3 text-right font-semibold text-success">15-30%</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <Separator />

                        {/* Section 4: Why Cold Applications Fail */}
                        <div>
                          <h3 className="font-semibold text-foreground mb-3">Why Cold Applications Fail</h3>
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-muted-foreground">250+ applicants per job—only 10-30 reach the hiring team</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-muted-foreground">Mass submissions often never reach a human</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-muted-foreground">ATS filters reject before a recruiter even sees your resume</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-muted-foreground">&lt;1% chance of progressing to offer stages</p>
                            </div>
                          </div>
                        </div>

                        {/* Section 5: Timing Matters */}
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                          <h3 className="font-semibold text-indigo-800 mb-2">Timing Matters: Get on the Radar Early</h3>
                          <p className="text-sm text-indigo-900/80">
                            <span className="font-bold">60-70% of hires</span> start before an opening is even advertised. 
                            Hiring managers engage candidates early and keep them warm. Getting on the radar of 
                            decision-makers early gives you a shot at roles before competitors even apply.
                          </p>
                        </div>
                      </div>
                    </ScrollArea>
                  </DialogContent>
                </Dialog>

                <span className="text-muted-foreground">|</span>

                {/* Link 2: What's included (Indigo themed) */}
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="inline-flex items-center text-indigo-600 hover:text-indigo-700 underline decoration-indigo-400 underline-offset-4 font-semibold transition-colors">
                      <Info className="w-4 h-4 mr-1.5" />
                      What's included →
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[85vh]">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-indigo-600">
                        <FileText className="w-5 h-5" />
                        What's Included in Your Inner Circle Report
                      </DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="max-h-[65vh] pr-4">
                      <div className="space-y-6">
                        {/* Company Intelligence */}
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                          <h3 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                            <Building2 className="w-5 h-5" />
                            Company Intelligence
                          </h3>
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-indigo-900">100 Best-Match Companies</p>
                                <p className="text-xs text-indigo-700/80">Curated based on your industry, target roles, location & culture fit</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-indigo-900">Company Snapshots</p>
                                <p className="text-xs text-indigo-700/80">Size, industry, growth trajectory, and why each company made your list</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-indigo-900">Hiring Pulse Indicator</p>
                                <p className="text-xs text-indigo-700/80">Recent hiring activity signals showing which companies are actively growing</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Contact Intelligence */}
                        <div>
                          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                            <UserCheck className="w-5 h-5 text-indigo-600" />
                            Contact Intelligence
                          </h3>
                          <div className="space-y-3">
                            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <Users className="w-5 h-5 text-success mt-0.5" />
                              <div>
                                <p className="font-medium text-sm">500-1,000 Decision-Maker Contacts</p>
                                <p className="text-xs text-muted-foreground">5-10 hand-selected contacts per company, chosen by hiring ability</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <Linkedin className="w-5 h-5 text-success mt-0.5" />
                              <div>
                                <p className="font-medium text-sm">LinkedIn Profile Links</p>
                                <p className="text-xs text-muted-foreground">Direct access to each contact's profile for immediate outreach</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <Target className="w-5 h-5 text-success mt-0.5" />
                              <div>
                                <p className="font-medium text-sm">Contact Relevance Tags</p>
                                <p className="text-xs text-muted-foreground">Hiring Manager, Dept Leader, Peer Insider, HR/TA, Executive Sponsor</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <Sparkles className="w-5 h-5 text-success mt-0.5" />
                              <div>
                                <p className="font-medium text-sm">"Why This Person Matters"</p>
                                <p className="text-xs text-muted-foreground">Brief explanation of strategic value for your specific background</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <Separator />

                        {/* Strategic Outreach Guidance */}
                        <div>
                          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                            <MessageSquare className="w-5 h-5 text-indigo-600" />
                            Strategic Outreach Guidance
                          </h3>
                          <div className="grid grid-cols-1 gap-3">
                            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                              <Crown className="w-4 h-4 text-indigo-600" />
                              <span className="text-sm">Contact Priority Tiers — Ranked by influence & response likelihood</span>
                            </div>
                            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                              <Briefcase className="w-4 h-4 text-indigo-600" />
                              <span className="text-sm">Role-Based Targeting Notes — Who to prioritize for specific roles</span>
                            </div>
                            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                              <BarChart3 className="w-4 h-4 text-indigo-600" />
                              <span className="text-sm">Outreach Sequence Recommendation — Who to reach first vs. warm up</span>
                            </div>
                          </div>
                        </div>

                        <Separator />

                        {/* What You'll Learn */}
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <h3 className="font-semibold text-purple-800 mb-3">What You'll Learn</h3>
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-purple-900/80">Which 100 companies are the strongest fit and actively building teams</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-purple-900/80">Who has authority to hire you or get your resume to the hiring manager</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-purple-900/80">Which contacts are peer-level insiders vs. senior sponsors</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-purple-900/80">How to prioritize outreach for maximum ROI on your job search</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </ScrollArea>
                  </DialogContent>
                </Dialog>
              </div>

              {/* 4 Feature Boxes */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 text-center border border-indigo-200 shadow-sm">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Network className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="font-semibold text-sm text-foreground">70-80% Hidden</div>
                  <div className="text-xs text-muted-foreground">Jobs filled via networking</div>
                </div>
                <div className="bg-white rounded-xl p-4 text-center border border-indigo-200 shadow-sm">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="font-semibold text-sm text-foreground">500-1,000</div>
                  <div className="text-xs text-muted-foreground">Decision-maker contacts</div>
                </div>
                <div className="bg-white rounded-xl p-4 text-center border border-indigo-200 shadow-sm">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="font-semibold text-sm text-foreground">100 Companies</div>
                  <div className="text-xs text-muted-foreground">Best-matched to you</div>
                </div>
                <div className="bg-white rounded-xl p-4 text-center border border-indigo-200 shadow-sm">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="font-semibold text-sm text-foreground">15-30% Response</div>
                  <div className="text-xs text-muted-foreground">vs. &lt;3% cold apps</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Search/Input Form */}
          <InnerCircleSearch />
        </div>
      </div>
  );
}
